import { endpointIdentity, resolveBadgeAttachments } from './badgeAttachments'
import { routeConnectionsGlobally } from './globalConnectionRouter'
import { connectionRoutingSettings, normalizeConnectionSpacing } from './connectionRoutingSettings'

export interface ProvisionalConnection {
    id: string
    from: string
    to?: string
    from_side: string
    to_side: string
}

function nodesOfType(layer: any, obj: string): any[] {
    return layer?.find?.((node: any) => node.obj === obj) ?? []
}

export function setConnectionSpacing(layer: any, value: unknown): number {
    const spacing = normalizeConnectionSpacing(value)
    if (layer?.setAttr) layer.setAttr('connection_spacing', spacing)
    else if (layer) layer.connection_spacing = spacing
    refreshBadgeConnections(layer, layer?.provisionalConnection ?? null)
    return spacing
}

export function refreshBadgeConnections(
    layer: any,
    provisional: ProvisionalConnection | null = null,
    connectionFilter: ((connection: any) => boolean) | null = null,
): any {
    const routingSettings = connectionRoutingSettings(layer)
    const badges = nodesOfType(layer, 'badge')
    const connections = nodesOfType(layer, 'connection')
    const descriptors = connections.flatMap(connection => [
        { connectionId: connection.id(), badgeId: connection.from, role: 'from', requestedSide: connection.from_side },
        { connectionId: connection.id(), badgeId: connection.to, role: 'to', requestedSide: connection.to_side },
    ])
    if (provisional?.from) {
        descriptors.push({
            connectionId: provisional.id, badgeId: provisional.from, role: 'from', requestedSide: provisional.from_side,
        })
        if (provisional.to) descriptors.push({
            connectionId: provisional.id, badgeId: provisional.to, role: 'to', requestedSide: provisional.to_side,
        })
    }
    const badgeReferences = badges.map(badge => ({
        id: badge.id(), geometry: badge.getConnectionGeometry(),
    }))
    const resolved = resolveBadgeAttachments(descriptors, badgeReferences, routingSettings)
    const byIdentity = new Map(resolved.map(attachment => [attachment.identity, attachment]))
    const globalRoutes = routeConnectionsGlobally(descriptors as any, badgeReferences, routingSettings)
    const routeById = new Map(globalRoutes.map(route => [route.id, route]))
    for (const connection of connections) {
        if (connectionFilter && !connectionFilter(connection)) continue
        const from = byIdentity.get(endpointIdentity({ connectionId: connection.id(), role: 'from' } as any))
        const to = byIdentity.get(endpointIdentity({ connectionId: connection.id(), role: 'to' } as any))
        const route = routeById.get(connection.id())
        connection.setResolvedGeometry(route?.from ?? from, route?.to ?? to, route?.points ?? [], route?.status ?? 'unresolved', route?.conflicts ?? [])
        if (route?.status === 'unresolved' && process.env.NODE_ENV !== 'production') {
            console.warn(`Connection routing unresolved for ${connection.id()} against ${route.conflicts.join(', ')}`)
        }
        if ((!from || !to) && process.env.NODE_ENV !== 'production') {
            console.warn(`Connection ${connection.id()} has an invalid badge endpoint`)
        }
    }
    if (!provisional) return null
    const from: any = byIdentity.get(`${provisional.id}:from`)
    const to: any = byIdentity.get(`${provisional.id}:to`)
    if (!from) return null
    const route = routeById.get(provisional.id)
    if (route?.status === 'unresolved' && process.env.NODE_ENV !== 'production') {
        console.warn(`Provisional connection routing unresolved for ${provisional.id} against ${route.conflicts.join(', ')}`)
    }
    return { from: route?.from ?? from, to: route?.to ?? to, points: route?.points ?? null,
        status: route?.status ?? (to ? 'unresolved' : 'pending'), conflicts: route?.conflicts ?? [] }
}
